import React, { useState, useEffect, useCallback } from 'react';
import { teamAPI } from '../services/api';
import { PageLayout, Avatar } from './ui';
import { useAuth } from '../context/AuthContext';

const TeamAdmin = () => {
  const { user } = useAuth();
  const companyId = user?.company_id;
  const [companyName, setCompanyName] = useState('');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Invite modal state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', first_name: '', last_name: '', role_ids: [] });
  const [inviting, setInviting] = useState(false);

  // Roles modal state
  const [showRoles, setShowRoles] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editRoleIds, setEditRoleIds] = useState([]);
  const [savingRoles, setSavingRoles] = useState(false);

  // Remove confirm state
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Initialise company name from auth context
  useEffect(() => {
    if (user?.company?.name) {
      setCompanyName(user.company.name);
    }
  }, [user]);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError('');
      const [usersRes, rolesRes] = await Promise.all([
        teamAPI.listUsers(companyId),
        teamAPI.getRoles(companyId),
      ]);
      setUsers(usersRes.users || []);
      setRoles(rolesRes.roles || []);
      if (usersRes.company) {
        setCompanyName(usersRes.company.name);
      }
    } catch (err) {
      setError(err.error || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  const showSuccessMessage = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  };

  // --- Invite ---
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email.trim()) return;
    try {
      setInviting(true);
      setError('');
      const res = await teamAPI.inviteUser(companyId, inviteForm);
      showSuccessMessage(res.message || 'User invited successfully');
      setShowInvite(false);
      setInviteForm({ email: '', first_name: '', last_name: '', role_ids: [] });
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const toggleInviteRole = (roleId) => {
    setInviteForm(prev => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter(id => id !== roleId)
        : [...prev.role_ids, roleId],
    }));
  };

  // --- Edit Roles ---
  const openEditRoles = async (user) => {
    setEditingUser(user);
    setEditRoleIds(user.roles.map(r => r.id));
    setShowRoles(true);
  };

  const handleSaveRoles = async () => {
    if (!editingUser) return;
    try {
      setSavingRoles(true);
      setError('');
      await teamAPI.setUserRoles(companyId, editingUser.id, editRoleIds);
      showSuccessMessage(`Roles updated for ${editingUser.email}`);
      setShowRoles(false);
      setEditingUser(null);
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to update roles');
    } finally {
      setSavingRoles(false);
    }
  };

  const toggleEditRole = (roleId) => {
    setEditRoleIds(prev =>
      prev.includes(roleId) ? prev.filter(id => id !== roleId) : [...prev, roleId]
    );
  };

  // --- Remove ---
  const handleRemove = async (user) => {
    try {
      setRemoving(true);
      setError('');
      await teamAPI.removeUser(companyId, user.id);
      showSuccessMessage(`${user.email} removed from the company`);
      setConfirmRemove(null);
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to remove user');
    } finally {
      setRemoving(false);
    }
  };

  if (!companyId) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-800">No company context found. Please log in again.</p>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      title="Team Members"
      subtitle={`Manage users in ${companyName}`}
      backTo="/management"
      backLabel="Back to Management"
      actions={
        <button
          onClick={() => setShowInvite(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-hover text-sm font-medium"
        >
          + Invite User
        </button>
      }
    >
      <div className="max-w-6xl">

      {/* Alerts */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 text-lg font-bold">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* User Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">Loading team members...</div>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-5xl mb-4">👥</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
          <p className="text-gray-500 mb-4">Invite users to get started.</p>
          <button onClick={() => setShowInvite(true)} className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-hover">
            Invite Your First User
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roles</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Avatar user={user} size="sm" />
                      <div>
                        <div className="font-medium text-gray-900">
                          {user.firstname || user.lastname
                            ? `${user.firstname || ''} ${user.lastname || ''}`.trim()
                            : user.email.split('@')[0]}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1.5">
                      {user.roles.length > 0 ? user.roles.map(role => (
                        <span key={role.id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-light text-primary">
                          {role.name}
                        </span>
                      )) : (
                        <span className="text-sm text-gray-400 italic">No roles assigned</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {user.is_primary && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Primary
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditRoles(user)}
                        className="text-sm text-primary hover:text-indigo-800 font-medium px-3 py-1.5 rounded hover:bg-primary-light"
                      >
                        Roles
                      </button>
                      <button
                        onClick={() => setConfirmRemove(user)}
                        className="text-sm text-red-600 hover:text-red-800 font-medium px-3 py-1.5 rounded hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-6 py-3 bg-gray-50 border-t text-sm text-gray-500">
            {users.length} member{users.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Invite User</h2>
              <button onClick={() => setShowInvite(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <form onSubmit={handleInvite}>
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      value={inviteForm.first_name}
                      onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                      placeholder="Jane"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={inviteForm.last_name}
                      onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                      placeholder="Doe"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assign Roles</label>
                  {roles.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No roles available for this company. Contact your administrator.</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                      {roles.map(role => (
                        <label key={role.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer border transition-colors ${
                          inviteForm.role_ids.includes(role.id) ? 'border-primary bg-primary-light' : 'border-transparent hover:bg-gray-50'
                        }`}>
                          <input
                            type="checkbox"
                            checked={inviteForm.role_ids.includes(role.id)}
                            onChange={() => toggleInviteRole(role.id)}
                            className="rounded text-primary"
                          />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{role.name}</div>
                            {role.description && <div className="text-xs text-gray-500">{role.description}</div>}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl flex justify-end gap-3">
                <button type="button" onClick={() => setShowInvite(false)} className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-100">
                  Cancel
                </button>
                <button type="submit" disabled={inviting} className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 font-medium">
                  {inviting ? 'Inviting...' : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Roles Modal */}
      {showRoles && editingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Edit Roles</h2>
              <p className="text-sm text-gray-500 mt-1">{editingUser.email}</p>
            </div>
            <div className="px-6 py-5">
              {roles.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No roles available for this company. Contact your administrator.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {roles.map(role => (
                    <label key={role.id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                      editRoleIds.includes(role.id) ? 'border-primary bg-primary-light' : 'border-gray-200 hover:bg-gray-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={editRoleIds.includes(role.id)}
                        onChange={() => toggleEditRole(role.id)}
                        className="rounded text-primary"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{role.name}</div>
                        {role.description && <div className="text-xs text-gray-500">{role.description}</div>}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <button onClick={() => { setShowRoles(false); setEditingUser(null); }} className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-100">
                Cancel
              </button>
              <button onClick={handleSaveRoles} disabled={savingRoles} className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 font-medium">
                {savingRoles ? 'Saving...' : 'Save Roles'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {confirmRemove && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-5 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Remove User</h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to remove <strong>{confirmRemove.email}</strong> from {companyName}?
                They will lose access to all company applications. This does not delete their account.
              </p>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <button onClick={() => setConfirmRemove(null)} disabled={removing} className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-100">
                Cancel
              </button>
              <button onClick={() => handleRemove(confirmRemove)} disabled={removing} className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium">
                {removing ? 'Removing...' : 'Remove User'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </PageLayout>
  );
};

export default TeamAdmin;
